/**
 * Generate smart pagination range with ellipsis for large page counts
 */
export interface PaginationRange {
  pages: (number | string)[];
  hasFirstEllipsis: boolean;
  hasLastEllipsis: boolean;
}

export const generatePaginationRange = (
  currentPage: number, 
  totalPages: number, 
  delta: number = 2
): PaginationRange => {
  if (totalPages <= 1) {
    return { pages: [1], hasFirstEllipsis: false, hasLastEllipsis: false };
  }

  const range: number[] = [];
  const rangeWithDots: (number | string)[] = [];
  
  // Calculate the range around current page
  for (let i = Math.max(2, currentPage - delta); 
       i <= Math.min(totalPages - 1, currentPage + delta); 
       i++) {
    range.push(i);
  }
  
  // Add first page and ellipsis if needed
  if (currentPage - delta > 2) {
    rangeWithDots.push(1, '...');
  } else {
    rangeWithDots.push(1);
  }
  
  // Add the calculated range
  rangeWithDots.push(...range);
  
  // Add last page and ellipsis if needed
  if (currentPage + delta < totalPages - 1) {
    rangeWithDots.push('...', totalPages);
  } else if (totalPages > 1) {
    rangeWithDots.push(totalPages);
  }
  
  return {
    pages: rangeWithDots,
    hasFirstEllipsis: rangeWithDots.includes('...'),
    hasLastEllipsis: rangeWithDots.lastIndexOf('...') !== rangeWithDots.indexOf('...')
  };
};

/**
 * Calculate pagination info for display
 */
export interface PaginationInfo {
  start: number;
  end: number;
  total: number;
  currentPage: number;
  totalPages: number;
}

export const calculatePaginationInfo = (
  currentPage: number,
  limit: number,
  total: number
): PaginationInfo => {
  const totalPages = Math.ceil(total / limit);
  const start = ((currentPage - 1) * limit) + 1;
  const end = Math.min(currentPage * limit, total);
  
  return {
    start,
    end,
    total,
    currentPage,
    totalPages
  };
};
